import {
    normalizeAeroWinRatePercent,
    type AeroCandidateReviewRow
} from '@/features/tradeLogicAuthoring/components/aeroCandidateResultDetails';

export interface AeroResultFilters {
    avgProfitMin: string;
    avgProfitMax: string;
    winRateMin: string;
    winRateMax: string;
    profitFactorMin: string;
    profitFactorMax: string;
    liveScoreMin: string;
    liveScoreMax: string;
    drawdownMin: string;
    drawdownMax: string;
    totalTradesMin: string;
    totalTradesMax: string;
}

export type AeroResultFilterKey = keyof AeroResultFilters;

export const createEmptyAeroResultFilters = (): AeroResultFilters => ({
    avgProfitMin: '',
    avgProfitMax: '',
    winRateMin: '',
    winRateMax: '',
    profitFactorMin: '',
    profitFactorMax: '',
    liveScoreMin: '',
    liveScoreMax: '',
    drawdownMin: '',
    drawdownMax: '',
    totalTradesMin: '',
    totalTradesMax: ''
});

export const parseAeroResultFilterNumber = (value: string): number | null => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return null;

    const parsedValue = Number(trimmedValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
};

export const matchesAeroResultRange = (value: number | null | undefined, minInput: string, maxInput: string): boolean => {
    const minValue = parseAeroResultFilterNumber(minInput);
    const maxValue = parseAeroResultFilterNumber(maxInput);

    if (minValue === null && maxValue === null) return true;
    if (value == null || !Number.isFinite(value)) return false;
    if (minValue !== null && value < minValue) return false;
    if (maxValue !== null && value > maxValue) return false;

    return true;
};

export const filterAeroResults = (results: AeroCandidateReviewRow[], filters: AeroResultFilters): AeroCandidateReviewRow[] =>
    results.filter(
        (candidate) =>
            matchesAeroResultRange(candidate.avgProfit, filters.avgProfitMin, filters.avgProfitMax) &&
            matchesAeroResultRange(normalizeAeroWinRatePercent(candidate.winRate), filters.winRateMin, filters.winRateMax) &&
            matchesAeroResultRange(candidate.profitFactor, filters.profitFactorMin, filters.profitFactorMax) &&
            matchesAeroResultRange(candidate.liveScore, filters.liveScoreMin, filters.liveScoreMax) &&
            matchesAeroResultRange(candidate.drawdown, filters.drawdownMin, filters.drawdownMax) &&
            matchesAeroResultRange(candidate.totalTrades, filters.totalTradesMin, filters.totalTradesMax)
    );

export const hasActiveAeroResultFilters = (filters: AeroResultFilters): boolean =>
    Object.values(filters).some((value) => value.trim().length > 0);

export const countActiveAeroResultFilters = (filters: AeroResultFilters): number => {
    const groups: [AeroResultFilterKey, AeroResultFilterKey][] = [
        ['avgProfitMin', 'avgProfitMax'],
        ['winRateMin', 'winRateMax'],
        ['profitFactorMin', 'profitFactorMax'],
        ['liveScoreMin', 'liveScoreMax'],
        ['drawdownMin', 'drawdownMax'],
        ['totalTradesMin', 'totalTradesMax']
    ];

    return groups.reduce(
        (total, [minKey, maxKey]) => total + (filters[minKey].trim().length > 0 || filters[maxKey].trim().length > 0 ? 1 : 0),
        0
    );
};

export interface AeroResultFilterGroup {
    label: string;
    valueLabel: string;
    minKey: AeroResultFilterKey;
    maxKey: AeroResultFilterKey;
    step: number;
}

export const AERO_RESULT_FILTER_GROUPS: AeroResultFilterGroup[] = [
    { label: 'Raw avg profit', valueLabel: '%', minKey: 'avgProfitMin', maxKey: 'avgProfitMax', step: 0.01 },
    { label: 'Win rate', valueLabel: '%', minKey: 'winRateMin', maxKey: 'winRateMax', step: 0.01 },
    { label: 'Profit factor', valueLabel: '', minKey: 'profitFactorMin', maxKey: 'profitFactorMax', step: 0.01 },
    { label: 'Composite score', valueLabel: '', minKey: 'liveScoreMin', maxKey: 'liveScoreMax', step: 0.01 },
    { label: 'Drawdown', valueLabel: '%', minKey: 'drawdownMin', maxKey: 'drawdownMax', step: 0.01 },
    { label: 'Trades', valueLabel: '', minKey: 'totalTradesMin', maxKey: 'totalTradesMax', step: 1 }
];

export interface AeroActiveFilterChip {
    id: string;
    label: string;
    minKey: AeroResultFilterKey;
    maxKey: AeroResultFilterKey;
}

export const buildActiveAeroFilterChips = (filters: AeroResultFilters): AeroActiveFilterChip[] => {
    const chips: AeroActiveFilterChip[] = [];

    for (const group of AERO_RESULT_FILTER_GROUPS) {
        const minValue = filters[group.minKey].trim();
        const maxValue = filters[group.maxKey].trim();
        if (!minValue && !maxValue) continue;

        const unit = group.valueLabel;

        let summary: string;
        if (minValue && maxValue) summary = `${minValue}${unit} – ${maxValue}${unit}`;
        else if (minValue) summary = `≥ ${minValue}${unit}`;
        else summary = `≤ ${maxValue}${unit}`;

        chips.push({
            id: group.label,
            label: `${group.label}: ${summary}`,
            minKey: group.minKey,
            maxKey: group.maxKey
        });
    }

    return chips;
};

export interface AeroResultFilterPreset {
    id: string;
    label: string;
    description: string;
    overrides: Partial<AeroResultFilters>;
}

export const AERO_RESULT_FILTER_PRESETS: AeroResultFilterPreset[] = [
    {
        id: 'profitable',
        label: 'Profitable',
        description: 'Profit factor ≥ 1',
        overrides: { profitFactorMin: '1' }
    },
    {
        id: 'solid-winners',
        label: 'Solid winners',
        description: 'Win rate ≥ 50% and profit factor ≥ 1.5',
        overrides: { winRateMin: '50', profitFactorMin: '1.5' }
    },
    {
        id: 'low-drawdown',
        label: 'Low drawdown',
        description: 'Drawdown ≤ 20%',
        overrides: { drawdownMax: '20' }
    }
];

export const applyAeroFilterPreset = (filters: AeroResultFilters, preset: AeroResultFilterPreset): AeroResultFilters => ({
    ...filters,
    ...preset.overrides
});

export const isAeroFilterPresetActive = (filters: AeroResultFilters, preset: AeroResultFilterPreset): boolean =>
    Object.entries(preset.overrides).every(([key, value]) => filters[key as AeroResultFilterKey] === value);

export const clearAeroFilterPreset = (filters: AeroResultFilters, preset: AeroResultFilterPreset): AeroResultFilters => {
    const next: AeroResultFilters = { ...filters };

    for (const key of Object.keys(preset.overrides) as AeroResultFilterKey[]) {
        next[key] = '';
    }

    return next;
};
